import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import "@/App.css";
import { ArrowUpRight, Music, Gem, Crown, Star, Globe, Layers, Zap, Gamepad2, Mic2, BookOpen, User, LogOut } from "lucide-react";
import { publishingApi } from "@/lib/api";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import Books from "@/pages/Books";
import BookDetail from "@/pages/BookDetail";
import MyLibrary from "@/pages/MyLibrary";
import Reader from "@/pages/Reader";
import PurchaseSuccess from "@/pages/PurchaseSuccess";
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from "@/pages/Auth";
import ProtectedRoute from "@/components/publishing/ProtectedRoute";

// Brand URLs
const JCROWN_URL = "https://www.kingjcrown.com";
const AFRQA_URL = "https://afrqa.com";
const UNIXORN_URL = "https://unixornworldwide.com";
const BARS_URL = "https://www.bearapstar.com";

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-black/80 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
        <a 
          href="/" 
          data-testid="nav-logo"
          className="text-white font-heading text-2xl tracking-tight hover:opacity-80 transition-opacity"
        >
          GOODMAN
        </a>
        
        <div className="hidden md:flex items-center gap-8">
          <a 
            href="#worlds" 
            data-testid="nav-worlds-link"
            className="text-white/60 hover:text-white transition-colors text-sm font-body tracking-wide"
          >
            Brand Worlds
          </a>
          <Link 
            to="/books"
            data-testid="nav-books-link"
            className="text-white/60 hover:text-white transition-colors text-sm font-body tracking-wide"
          >
            Books
          </Link>
          {user ? (
            <Link 
              to="/my-library"
              data-testid="nav-my-library-link"
              className="text-white hover:text-jcrown-gold transition-colors text-sm font-body tracking-wide flex items-center gap-1"
            >
              My Library
            </Link>
          ) : (
            <Link 
              to="/login"
              data-testid="nav-signin-link"
              className="text-white/60 hover:text-white transition-colors text-sm font-body tracking-wide"
            >
              Sign In
            </Link>
          )}
          <a 
            href="#ecosystem" 
            data-testid="nav-ecosystem-link"
            className="text-white/60 hover:text-white transition-colors text-sm font-body tracking-wide"
          >
            Ecosystem
          </a>
          <a 
            href={JCROWN_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-jcrown-link"
            className="text-jcrown-gold hover:text-white transition-colors text-sm font-body tracking-wide flex items-center gap-1"
          >
            J.CROWN
            <ArrowUpRight className="w-3 h-3" />
          </a>
        </div>

        {/* Mobile Menu */}
        <div className="md:hidden flex items-center gap-4">
          <Link 
            to="/books"
            data-testid="mobile-nav-books-link"
            className="text-white text-sm font-body"
          >
            Books
          </Link>
          <a 
            href={JCROWN_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-nav-jcrown-link"
            className="text-jcrown-gold text-sm font-body flex items-center gap-1"
          >
            J.CROWN
            <ArrowUpRight className="w-3 h-3" />
          </a>
        </div>
      </div>
    </nav>
  );
};

// Hero Section - Updated for Ecosystem
const HeroSection = () => {
  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-primary" />
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/13230484/pexels-photo-13230484.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      
      {/* Gradient Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-black/50" />
      
      {/* Content */}
      <div className="relative z-10 text-center px-6 py-32">
        <p className="text-white/40 text-xs tracking-[0.3em] uppercase mb-8 font-body animate-fade-in">
          A Creative Universe
        </p>
        
        <h1 className="font-heading text-white text-5xl sm:text-7xl md:text-8xl lg:text-[9rem] leading-[0.9] tracking-tighter uppercase animate-slide-up">
          GOODMAN
        </h1>
        <h2 className="font-heading text-white/20 text-3xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tighter uppercase mt-2 animate-slide-up" style={{ animationDelay: '0.1s' }}>
          ENTERTAINMENT
        </h2>
        
        <p className="mt-10 text-white/50 font-body text-base md:text-lg max-w-2xl mx-auto leading-relaxed animate-fade-in" style={{ animationDelay: '0.3s' }}>
          A modern creative empire housing music, fashion, technology, and cultural futures. Independent brand houses united under one universe.
        </p>
        
        <div className="mt-12 flex flex-wrap justify-center gap-4 animate-fade-in" style={{ animationDelay: '0.5s' }}>
          <a 
            href="#worlds"
            data-testid="hero-explore-btn"
            className="group bg-white text-black px-8 py-4 font-body text-sm tracking-wider hover:bg-white/90 transition-all active:scale-95 flex items-center gap-2"
          >
            EXPLORE BRAND WORLDS
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
        </div>

        {/* Brand Indicators */}
        <div className="mt-20 flex flex-wrap justify-center items-center gap-6 md:gap-12 animate-fade-in" style={{ animationDelay: '0.7s' }}>
          <span className="text-afrqa-gold text-xs tracking-[0.2em] uppercase font-body opacity-60">AFRQA</span>
          <span className="w-1 h-1 bg-white/20 rounded-full hidden md:block" />
          <span className="text-unixorn-cream text-xs tracking-[0.2em] uppercase font-body opacity-60">UNIXORN</span>
          <span className="w-1 h-1 bg-white/20 rounded-full hidden md:block" />
          <span className="text-bars-cyan text-xs tracking-[0.2em] uppercase font-body opacity-60">B.A.R.S.</span>
          <span className="w-1 h-1 bg-white/20 rounded-full hidden md:block" />
          <span className="text-jcrown-gold text-xs tracking-[0.2em] uppercase font-body opacity-60">J.CROWN</span>
        </div>
      </div>
      
      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <span className="text-white/20 text-[10px] tracking-[0.3em] uppercase font-body">Discover</span>
        <div className="w-px h-16 bg-gradient-to-b from-white/30 to-transparent animate-pulse" />
      </div>
    </section>
  );
};

// Brand Worlds Section - AFRQA, UNIXORN & B.A.R.S.
const BrandWorldsSection = () => {
  return (
    <section 
      id="worlds"
      data-testid="brand-worlds-section"
      className="relative bg-primary py-24 md:py-40"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-20">
          <p className="text-white/40 text-xs tracking-[0.3em] uppercase mb-4 font-body">
            The Goodman Universe
          </p>
          <h2 className="font-heading text-white text-4xl md:text-6xl lg:text-7xl tracking-tighter uppercase">
            BRAND WORLDS
          </h2>
          <p className="mt-6 text-white/40 font-body text-sm md:text-base max-w-xl mx-auto">
            Independent luxury houses. Distinct creative visions. One universe.
          </p>
        </div>
        
        {/* Brand Cards Grid - 3 Column */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          
          {/* AFRQA Card - Black + Gold Luxury */}
          <a 
            href={AFRQA_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="afrqa-card"
            className="group relative overflow-hidden cursor-pointer"
          >
            <div className="afrqa-card relative bg-black border border-afrqa-gold/20 hover:border-afrqa-gold/50 transition-all duration-700 overflow-hidden">
              {/* Background Image */}
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 group-hover:scale-110"
                style={{
                  backgroundImage: `url('https://images.unsplash.com/photo-1618005198919-d3d4b5a92ead?w=800&q=80')`,
                }}
              />
              {/* Gold Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/40" />
              <div className="absolute inset-0 bg-gradient-to-br from-afrqa-gold/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              
              {/* Content */}
              <div className="relative z-10 p-8 md:p-10 min-h-[400px] md:min-h-[480px] flex flex-col justify-end">
                <div className="flex items-center gap-3 mb-4">
                  <Crown className="w-5 h-5 text-afrqa-gold" />
                  <span className="text-afrqa-gold text-[10px] tracking-[0.3em] uppercase font-body">
                    Afro-Futurist Luxury
                  </span>
                </div>
                
                <h3 className="font-heading text-white text-4xl md:text-5xl tracking-tighter uppercase mb-3 group-hover:text-afrqa-gold transition-colors duration-500">
                  AFRQA™
                </h3>
                
                <p className="text-white/50 font-body text-sm leading-relaxed max-w-md mb-6 group-hover:text-white/70 transition-colors duration-500">
                  Future culture. Cinematic luxury. Editorial collectibles.
                </p>
                
                <div className="flex items-center gap-3 text-afrqa-gold font-body text-sm tracking-wider group-hover:gap-5 transition-all duration-500">
                  <span>ENTER AFRQA™</span>
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </div>
            </div>
          </a>
          
          {/* UNIXORN Card - Cream Monochrome Luxury */}
          <a 
            href={UNIXORN_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="unixorn-card"
            className="group relative overflow-hidden cursor-pointer"
          >
            <div className="unixorn-card relative bg-unixorn-cream border border-unixorn-dark/10 hover:border-unixorn-dark/30 transition-all duration-700 overflow-hidden">
              {/* Background Image */}
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 group-hover:scale-110"
                style={{
                  backgroundImage: `url('https://images.unsplash.com/photo-1551488831-00ddcb6c6bd3?w=800&q=80')`,
                }}
              />
              {/* Cream Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-unixorn-cream via-unixorn-cream/95 to-unixorn-cream/60" />
              
              {/* Content */}
              <div className="relative z-10 p-8 md:p-10 min-h-[400px] md:min-h-[480px] flex flex-col justify-end">
                <div className="flex items-center gap-3 mb-4">
                  <Star className="w-5 h-5 text-unixorn-dark/70" />
                  <span className="text-unixorn-dark/60 text-[10px] tracking-[0.3em] uppercase font-body">
                    Mythic Minimalism
                  </span>
                </div>
                
                <h3 className="font-heading text-unixorn-dark text-4xl md:text-5xl tracking-tighter uppercase mb-3 group-hover:text-black transition-colors duration-500">
                  UNIXORN™
                </h3>
                
                <p className="text-unixorn-dark/60 font-body text-sm leading-relaxed max-w-md mb-6 group-hover:text-unixorn-dark/80 transition-colors duration-500">
                  Celestial energy. Sculpted fashion. Mythic luxury.
                </p>
                
                <div className="flex items-center gap-3 text-unixorn-dark font-body text-sm tracking-wider group-hover:gap-5 transition-all duration-500">
                  <span>ENTER UNIXORN™</span>
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </div>
            </div>
          </a>
          
          {/* B.A.R.S. Card - Energetic Music Gaming */}
          <a 
            href={BARS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="bars-card"
            className="group relative overflow-hidden cursor-pointer"
          >
            <div className="bars-card relative bg-bars-dark border border-bars-cyan/20 hover:border-bars-cyan/60 transition-all duration-700 overflow-hidden">
              {/* Background Image */}
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 group-hover:scale-110"
                style={{
                  backgroundImage: `url('https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&q=80')`,
                }}
              />
              {/* Energetic Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-bars-dark via-bars-dark/90 to-bars-dark/50" />
              <div className="absolute inset-0 bg-gradient-to-br from-bars-cyan/10 via-bars-purple/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              
              {/* Animated Glow Effect */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute inset-0 border border-bars-cyan/40 shadow-[inset_0_0_30px_rgba(0,245,255,0.1)]" />
              </div>
              
              {/* Content */}
              <div className="relative z-10 p-8 md:p-10 min-h-[400px] md:min-h-[480px] flex flex-col justify-end">
                <div className="flex items-center gap-3 mb-4">
                  <Gamepad2 className="w-5 h-5 text-bars-cyan" />
                  <span className="text-bars-cyan text-[10px] tracking-[0.3em] uppercase font-body">
                    Interactive Entertainment
                  </span>
                </div>
                
                <h3 className="font-heading text-white text-4xl md:text-5xl tracking-tighter uppercase mb-3 group-hover:text-bars-cyan transition-colors duration-500">
                  B.A.R.S.™
                </h3>
                
                <p className="text-white/60 font-body text-sm leading-relaxed max-w-md mb-2 group-hover:text-white/80 transition-colors duration-500">
                  Music. Culture. Competition.
                </p>
                <p className="text-bars-cyan/70 font-body text-xs tracking-wide mb-6">
                  Be A Rap Star.
                </p>
                
                <div className="flex items-center gap-3 text-bars-cyan font-body text-sm tracking-wider group-hover:gap-5 transition-all duration-500">
                  <span>ENTER B.A.R.S.™</span>
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </div>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
};

// KING J.CROWN Music Section
const JCrownMusicSection = () => {
  return (
    <section 
      id="jcrown"
      data-testid="jcrown-section"
      className="relative bg-jcrown-dark py-24 md:py-32 overflow-hidden"
    >
      {/* Royal Cosmic Background Effect */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-gradient-radial from-jcrown-purple/20 via-jcrown-gold/10 to-transparent blur-3xl" />
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Visual - Album Cover */}
          <div className="relative group order-2 lg:order-1">
            <div className="relative overflow-hidden">
              <img 
                src="https://customer-assets-agu9un31.emergentagent.net/job_goodman-health-check/artifacts/7mlkbqe0_KING%20_COVER.PNG"
                alt="J.Crown - KING"
                className="w-full aspect-square object-cover transition-transform duration-700 group-hover:scale-[1.02]"
              />
              <div className="absolute inset-0 border border-jcrown-gold/20 group-hover:border-jcrown-gold/50 transition-colors duration-500" />
              
              {/* Gold shimmer overlay */}
              <div className="absolute inset-0 bg-gradient-to-tr from-jcrown-gold/0 via-jcrown-gold/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
            </div>
            
            {/* Album Info */}
            <div className="mt-6 flex items-center justify-between">
              <div>
                <p className="text-jcrown-gold/60 text-[10px] tracking-[0.3em] uppercase font-body mb-1">
                  Latest Release
                </p>
                <p className="text-white font-heading text-2xl tracking-tight uppercase">
                  KING
                </p>
              </div>
              <p className="text-white/40 font-body text-xs tracking-widest uppercase">
                MMXXVI
              </p>
            </div>
          </div>
          
          {/* Content */}
          <div className="order-1 lg:order-2">
            <div className="flex items-center gap-3 mb-6">
              <Crown className="w-5 h-5 text-jcrown-gold" />
              <span className="text-jcrown-gold text-[10px] tracking-[0.3em] uppercase font-body">
                Music Artist
              </span>
            </div>
            
            <h2 className="font-heading text-white text-5xl md:text-7xl lg:text-8xl tracking-tighter uppercase mb-2 leading-[0.9]">
              KING
            </h2>
            <h2 className="font-heading text-jcrown-gold text-4xl md:text-5xl lg:text-6xl tracking-tight uppercase mb-8 leading-[0.9]">
              J.CROWN
            </h2>
            
            <p className="text-jcrown-gold/90 font-body text-sm tracking-[0.15em] uppercase mb-6">
              Keep Inspiring New Generations
            </p>
            
            <p className="text-white/60 font-body text-base md:text-lg leading-relaxed mb-10 max-w-lg">
              A regal voice in modern music. J.Crown crafts anthems of ambition, resilience, and legacy — inspiring the next generation to rise, reign, and rewrite the rules.
            </p>
            
            <a 
              href={JCROWN_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="jcrown-visit-button"
              className="group inline-flex items-center gap-3 bg-jcrown-gold text-jcrown-dark px-8 py-4 font-body text-sm tracking-wider hover:bg-white hover:text-black transition-all duration-300 active:scale-95"
            >
              ENTER THE KINGDOM
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Creative Ecosystem Section
const EcosystemSection = () => {
  const pillars = [
    { icon: Music, label: "Music", desc: "Sound & releases" },
    { icon: Gem, label: "Fashion", desc: "Luxury apparel" },
    { icon: Zap, label: "Technology", desc: "Digital futures" },
    { icon: Layers, label: "Storytelling", desc: "Cinematic worlds" },
    { icon: Globe, label: "Culture", desc: "Future movements" },
  ];

  return (
    <section 
      id="ecosystem"
      data-testid="ecosystem-section"
      className="relative bg-tertiary py-24 md:py-40"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left - Content */}
          <div>
            <p className="text-white/40 text-xs tracking-[0.3em] uppercase mb-4 font-body">
              Creative Systems
            </p>
            <h2 className="font-heading text-white text-4xl md:text-5xl lg:text-6xl tracking-tighter uppercase mb-8">
              THE GOODMAN
              <br />
              <span className="text-white/20">UNIVERSE</span>
            </h2>
            
            <p className="text-white/50 font-body text-base md:text-lg leading-relaxed mb-6 max-w-lg">
              A luxury multimedia house connecting independent creative worlds. Music, fashion, technology, storytelling, and future culture—unified under one creative empire.
            </p>
            
            <p className="text-white/30 font-body text-sm leading-relaxed max-w-lg">
              Each brand house operates with complete creative independence while contributing to a larger cultural ecosystem designed for the future.
            </p>
          </div>
          
          {/* Right - Pillars */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
            {pillars.map((pillar, index) => (
              <div 
                key={pillar.label}
                className="group p-6 border border-white/5 hover:border-white/20 bg-white/[0.02] hover:bg-white/[0.05] transition-all duration-500"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <pillar.icon className="w-5 h-5 text-white/30 group-hover:text-white/60 transition-colors duration-500 mb-4" />
                <h3 className="font-heading text-white text-lg tracking-tight uppercase mb-1">
                  {pillar.label}
                </h3>
                <p className="text-white/30 font-body text-xs">
                  {pillar.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer 
      data-testid="main-footer"
      className="relative bg-primary border-t border-white/5 py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          {/* Brand */}
          <div className="md:col-span-5">
            <h2 className="font-heading text-white text-5xl md:text-7xl tracking-tighter uppercase mb-4">
              GOODMAN
            </h2>
            <p className="text-white/30 font-body text-sm max-w-sm">
              A modern creative empire. Independent brand houses. Luxury futures.
            </p>
          </div>
          
          {/* Brand Worlds */}
          <div className="md:col-span-3">
            <h3 className="text-white/30 text-[10px] tracking-[0.3em] uppercase mb-6 font-body">
              Brand Worlds
            </h3>
            <ul className="space-y-4">
              <li>
                <a 
                  href={AFRQA_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-afrqa-link"
                  className="text-afrqa-gold/80 hover:text-afrqa-gold transition-colors font-body text-sm flex items-center gap-2"
                >
                  AFRQA™
                  <ArrowUpRight className="w-3 h-3" />
                </a>
              </li>
              <li>
                <a 
                  href={UNIXORN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-unixorn-link"
                  className="text-unixorn-cream/80 hover:text-unixorn-cream transition-colors font-body text-sm flex items-center gap-2"
                >
                  UNIXORN™
                  <ArrowUpRight className="w-3 h-3" />
                </a>
              </li>
              <li>
                <a 
                  href={BARS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-bars-link"
                  className="text-bars-cyan/80 hover:text-bars-cyan transition-colors font-body text-sm flex items-center gap-2"
                >
                  B.A.R.S.™
                  <ArrowUpRight className="w-3 h-3" />
                </a>
              </li>
              <li>
                <a 
                  href={JCROWN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-jcrown-link"
                  className="text-jcrown-gold/80 hover:text-jcrown-gold transition-colors font-body text-sm flex items-center gap-2"
                >
                  KING J.CROWN
                  <ArrowUpRight className="w-3 h-3" />
                </a>
              </li>
            </ul>
          </div>
          
          {/* Connect */}
          <div className="md:col-span-2">
            <h3 className="text-white/30 text-[10px] tracking-[0.3em] uppercase mb-6 font-body">
              Connect
            </h3>
            <ul className="space-y-4">
              <li>
                <a 
                  href="#ecosystem"
                  data-testid="footer-about-link"
                  className="text-white/50 hover:text-white transition-colors font-body text-sm"
                >
                  About
                </a>
              </li>
              <li>
                <a 
                  href="mailto:contact@goodman-entertainment.com"
                  data-testid="footer-contact-link"
                  className="text-white/50 hover:text-white transition-colors font-body text-sm"
                >
                  Contact
                </a>
              </li>
            </ul>
          </div>
          
          {/* Ecosystem */}
          <div className="md:col-span-2">
            <h3 className="text-white/30 text-[10px] tracking-[0.3em] uppercase mb-6 font-body">
              Ecosystem
            </h3>
            <ul className="space-y-4">
              <li><span className="text-white/30 font-body text-sm">Music</span></li>
              <li><span className="text-white/30 font-body text-sm">Fashion</span></li>
              <li><span className="text-white/30 font-body text-sm">Culture</span></li>
            </ul>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="mt-20 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <p className="text-white/20 font-body text-xs">
            © 2026 Goodman Entertainment. All rights reserved.
          </p>
          <p className="text-white/20 font-body text-xs tracking-wider">
            LUXURY FUTURES™
          </p>
        </div>
      </div>
    </footer>
  );
};

function App() {
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/books" element={<Books />} />
            <Route path="/books/:slug" element={<BookDetail />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/my-library" element={<ProtectedRoute><MyLibrary /></ProtectedRoute>} />
            <Route path="/read/:slug" element={<Reader />} />
            <Route path="/purchase/success" element={<PurchaseSuccess />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

// Publishing section for homepage - "Original Stories. Original Worlds."
const PublishingHomeSection = () => {
  const [books, setBooks] = useState([]);

  useEffect(() => {
    publishingApi.listBooks().then(data => setBooks(data.books)).catch(() => {});
  }, []);

  return (
    <section 
      id="publishing"
      data-testid="publishing-home-section"
      className="relative bg-secondary py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-jcrown-gold/10 blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <BookOpen className="w-4 h-4 text-jcrown-gold" />
              <p className="text-jcrown-gold/70 text-[10px] tracking-[0.4em] uppercase font-body">
                Goodman Publishing
              </p>
            </div>
            <h2 className="font-heading text-white text-4xl md:text-6xl lg:text-7xl tracking-tighter uppercase leading-[0.9]">
              ORIGINAL STORIES.
              <br />
              <span className="text-white/30">ORIGINAL WORLDS.</span>
            </h2>
            <p className="mt-6 text-white/50 font-body text-base md:text-lg max-w-xl">
              Discover the Johnny Crown Library — six original books spanning fantasy, superheroes, culture, adventure and imagination.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Link 
              to="/books"
              data-testid="home-explore-books-btn"
              className="group bg-jcrown-gold text-jcrown-dark px-6 py-3 font-body text-xs tracking-widest uppercase hover:bg-white hover:text-black transition-all active:scale-95 inline-flex items-center gap-2"
            >
              Explore All Books
              <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
            <Link 
              to="/books#complete-bundle"
              data-testid="home-collection-btn"
              className="group border border-white/20 text-white px-6 py-3 font-body text-xs tracking-widest uppercase hover:border-white hover:bg-white/5 transition-all active:scale-95 inline-flex items-center gap-2"
            >
              Complete Collection
            </Link>
          </div>
        </div>

        {/* Book covers strip */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4 md:gap-6">
          {books.map((book, i) => (
            <Link
              key={book.id}
              to={`/books/${book.slug}`}
              data-testid={`home-book-${book.slug}`}
              className="group relative overflow-hidden bg-black/40 flex items-center justify-center"
              style={{ aspectRatio: '2 / 3', animationDelay: `${i * 0.05}s` }}
            >
              <img 
                src={book.cover_image}
                alt={book.title_display}
                loading="lazy"
                className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-70 group-hover:opacity-90 transition-opacity" />
              <div className="absolute inset-x-0 bottom-0 p-3">
                <p className="text-white font-heading text-xs md:text-sm tracking-tight uppercase leading-tight">
                  {book.title_display}
                </p>
              </div>
              <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{ boxShadow: `inset 0 0 0 1px ${book.accent_color}80` }}
              />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// Home page
const HomePage = () => {
  return (
    <div className="bg-primary min-h-screen">
      <div className="noise-overlay" />
      <Navigation />
      <HeroSection />
      <BrandWorldsSection />
      <JCrownMusicSection />
      <PublishingHomeSection />
      <EcosystemSection />
      <Footer />
    </div>
  );
};

export default App;
